import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { YesNoField } from '@/components/form/fields/yesNo/YesNoInput';

import { Form } from '@/components/form/Form';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { useCompany } from '@/services/api/hooks/companies';
import { useSubmitWarrantyRequest } from '@/services/api/hooks/management';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';

const FormFields = z.object({
  customerName: z.string().nonempty(),
  modelAndManufacturer: z.string().nonempty(),
  serialNumber: z.string().nonempty(),
  userFullName: z.string().nonempty(),
  userAddress: z.string().nonempty(),
  workEmailAddress: z.string().email().nonempty(),
  mobileNumber: z.string().nonempty(),
  canPrintLabel: z.boolean().optional(),
  notes: z.string().optional()
});

type FormFields = z.infer<typeof FormFields>;

export const WarrantyReplacementPage: FC = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: 'Management',
      to: ''
    },
    {
      name: 'Warranty Replacement',
      to: ''
    }
  ];

  const { partnerId, companyId } = useParams();

  if (!(partnerId && companyId)) throw new Error('Missing params');
  const [company] = useCompany(partnerId, companyId);

  const methods = useForm<FormFields>({
    resolver: zodResolver(FormFields),
    defaultValues: {
      customerName: company.name
    }
  });

  const navigate = useNavigate();
  const routePrefix = useCustomerRoutePrefix();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const submitRequest = useSubmitWarrantyRequest();

  const onSubmit = async (data: FormFields) => {
    setLoading(true);
    setErrorMessage('');

    const result = await submitRequest.mutateAsync({
      customerName: data.customerName,
      modelAndManufacturer: data.modelAndManufacturer,
      serialNumber: data.serialNumber,
      userFullName: data.userFullName,
      userAddress: data.userAddress,
      workEmailAddress: data.workEmailAddress,
      mobileNumber: data.mobileNumber,
      canPrintLabel: data.canPrintLabel || false,
      notes: data.notes
    });

    if (result.status === 'Message sent.') {
      navigate(`${routePrefix}/management/success`);
    } else {
      setErrorMessage('Unable to submit request.');
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Warranty Replacement</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto p-10">
        <Form methods={methods} onSubmit={onSubmit} className="space-y-5">
          <TextField id="customerName" label="Customer Name" readonly />
          <TextField id="modelAndManufacturer" label="Device Model and Manufacturer" placeholder="Dell Latitude 7550" />
          <TextField id="serialNumber" label="Device Serial Number" />
          <TextField id="userFullName" label="User's Full Name" />
          <TextAreaField
            id="userAddress"
            label="User's Address"
            placeholder="Full address for delivery and collection"
          />
          <TextField id="workEmailAddress" label="Work Email" />
          <TextField id="mobileNumber" label="Mobile Number" />
          <YesNoField id="canPrintLabel" label="Can the user print a label?" />
          <TextAreaField
            id="notes"
            label="Notes / Special Requirements"
            placeholder="Please include details of the issue"
          />
          {errorMessage && <p className="text-red-500">{errorMessage}</p>}
          <Button variant={ButtonVariant.Primary} type="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </div>
    </>
  );
};
